import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Menu, MenuItem } from "@mui/material";
import {
	icon,
	IconComponent,
	createNotification,
	CustomButton,
	CustomIconButton,
	Preview,
	CustomTooltip,
} from "../../../../common/components";
import { hasPermission, Permissions, isSegFeatureEnabled, SegFlags, translate } from "../../../../common/providers";
import { ApiService, CommitmentService } from "../../../../api";
import AssociateContentDialog from "../associate-content-dialog/AssociateContentDialog";
import ConfirmationDialog from "../commitment-sidepanel/ConfirmationDialog";
import { getPilotDisplayName, formatDate } from "../../utils/dataTransformers";
import {
	deleteCommitments,
	updateThemeList,
	setSidePanelOpen,
	setSelectedCommitment,
	setRightPanelView,
	fetchCommitments,
	setPage,
	decreaseThemeCount,
	setCommitment,
} from "../../slice/commitmentTrackingSlice";
import { translateFilterOptionLabel } from "../../utils/translate-utils";
import styles from "./CommitmentDetails.module.css";
import { useApi } from "../../../../common/hooks";
import { isNonEmptyArray } from "../../../../common/utils";

export default function CommitmentDetails() {
	const dispatch = useDispatch();
	const projectId = useSelector(({ context }) => context?.project?.id);
	const cancelTokenSourceRef = useRef(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [isContentExpanded, setIsContentExpanded] = useState(true);
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleteContentDialogOpen, setDeleteContentDialogOpen] = useState(false);
	const [contentToDelete, setContentToDelete] = useState(null);
	const [sources, setSources] = useState([]);
	const [isLoadingSources, setIsLoadingSources] = useState(false);
	const [preview, setPreview] = useState(null);
	const selectedCommitment = useSelector(({ commitmentTracking }) => commitmentTracking.selectedCommitment);
	const appliedFilters = useSelector(({ commitmentTracking }) => commitmentTracking.appliedFilters);
	const themeFieldUuid = useSelector(({ commitmentTracking }) => commitmentTracking.themeFieldUuid);
	const selectedRows = useSelector(({ commitmentTracking }) => commitmentTracking.selectedRows);
	const selectedTheme = useSelector(({ commitmentTracking }) => commitmentTracking.selectedTheme);
	const selectedThemeUuid = useSelector(({ commitmentTracking }) => commitmentTracking.selectedThemeUuid);
	const filterDefinitions = useSelector(({ commitmentTracking }) => commitmentTracking.filterDefinitions);
	const launchProgress = useSelector(({ commitmentTracking }) => commitmentTracking.progress);
	const limit = useSelector(({ commitmentTracking }) => commitmentTracking.limit);
	const { call: associateContent } = useApi(CommitmentService.associateContent);
	const { call: getSources } = useApi(CommitmentService.getSources);
	const { call: deleteSource } = useApi(CommitmentService.deleteSource);
	const { call: likeDetectedContent } = useApi(CommitmentService.likeDetectedContent);
	const hasRights = useMemo(() => hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]), []);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	const fetchSources = useCallback(async () => {
		if (!selectedCommitment?.commitmentId) {
			setSources([]);
			return [];
		}
		setIsLoadingSources(true);
		try {
			const response = await getSources({ projectId, commitmentId: selectedCommitment.commitmentId });
			const data = response?.data || response || [];
			setSources(isNonEmptyArray(data) ? data : []);
			return data;
		} catch (error) {
			console.error("Error fetching sources:", error);
			setSources([]);
			return [];
		} finally {
			setIsLoadingSources(false);
		}
	}, [projectId, selectedCommitment?.commitmentId, getSources]);
	useEffect(() => {
		fetchSources();
	}, [fetchSources]);
	const handleMenuClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleMenuClose = () => {
		setAnchorEl(null);
	};
	const handleEditClick = () => {
		dispatch(setRightPanelView("edit"));
		handleMenuClose();
	};
	const handleDeleteClick = useCallback(() => {
		setDeleteDialogOpen(true);
		handleMenuClose();
	}, []);
	const handleDeleteConfirm = useCallback(async () => {
		if (!selectedCommitment) {
			return;
		}
		try {
			const cancelToken = cancelTokenSourceRef.current?.token;
			const smartDeletePayload = {
				filter: {},
				includeIds: [selectedCommitment.commitmentId],
				excludeIds: [],
			};
			await dispatch(
				deleteCommitments({
					projectId,
					payload: smartDeletePayload,
					cancelToken,
				})
			).unwrap();
			if (selectedTheme) {
				dispatch(decreaseThemeCount({ themeName: selectedTheme, count: 1 }));
			}
			dispatch(setSidePanelOpen(false));
			dispatch(setSelectedCommitment(null));
			createNotification({
				type: "success",
				message: translate("commitment-tracking.details.delete-success"),
			});
			setDeleteDialogOpen(false);
			const payload = {};
			if (selectedThemeUuid && themeFieldUuid) {
				const themeField = filterDefinitions.find((field) => field.fieldUuid === themeFieldUuid);
				const themeFieldName = themeField ? themeField.fieldName.toLowerCase().replace(/ /g, "_") : "theme";
				payload[themeFieldName] = {
					is: true,
					matchAll: true,
					elements: [selectedThemeUuid],
				};
			}
			dispatch(setPage(0));
			await dispatch(fetchCommitments({ projectId, payload, page: 0, limit, cancelToken })).unwrap();
			await dispatch(
				updateThemeList({ projectId, filters: appliedFilters, themeFieldUuid, cancelToken })
			).unwrap();
		} catch (error) {
			console.error("Error deleting commitment:", error);
			createNotification({
				type: "error",
				message: translate("commitment-tracking.details.delete-error"),
			});
			setDeleteDialogOpen(false);
		}
	}, [
		dispatch,
		selectedCommitment,
		projectId,
		themeFieldUuid,
		appliedFilters,
		selectedTheme,
		selectedThemeUuid,
		filterDefinitions,
		limit,
	]);
	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
	};
	const toggleContentSection = () => {
		setIsContentExpanded(!isContentExpanded);
	};
	const handleAssociateContent = () => {
		setLinkDialogOpen(true);
	};
	const handleLinkDialogClose = () => {
		setLinkDialogOpen(false);
	};
	const handleDeleteContentClick = (informationId) => {
		setContentToDelete(informationId);
		setDeleteContentDialogOpen(true);
	};
	const handleDeleteContentConfirm = async () => {
		if (!contentToDelete || !selectedCommitment?.commitmentId) {
			return;
		}
		try {
			await deleteSource({
				projectId,
				commitmentId: selectedCommitment.commitmentId,
				informationId: contentToDelete,
			});
			createNotification({
				type: "success",
				message: translate("commitment-tracking.details.delete-content-success"),
			});
			dispatch(
				setCommitment({
					commitmentId: selectedCommitment.commitmentId,
					field: "sourceCount",
					value: sources.length - 1,
				})
			);
			setSources((prev) => prev.filter((source) => source.informationId !== contentToDelete));
		} catch (error) {
			console.error("Error deleting content:", error);
			createNotification({
				type: "error",
				message: translate("commitment-tracking.details.delete-content-error"),
			});
		} finally {
			setDeleteContentDialogOpen(false);
			setContentToDelete(null);
		}
	};
	const handleDeleteContentCancel = () => {
		setDeleteContentDialogOpen(false);
		setContentToDelete(null);
	};
	const handleCommentsClick = () => {
		dispatch(setRightPanelView("comments"));
		handleMenuClose();
	};
	const handleHistoryClick = () => {
		dispatch(setRightPanelView("history"));
		handleMenuClose();
	};
	const handleLinkSubmit = async ({ informationId, type }) => {
		try {
			await associateContent(
				{
					projectId,
					commitmentId: selectedCommitment.commitmentId,
				},
				{
					informationId,
					type,
				}
			);
			createNotification({
				type: "success",
				message: translate("commitment-tracking.details.link-success"),
			});
			setLinkDialogOpen(false);
			const response = await fetchSources();
			dispatch(
				setCommitment({
					commitmentId: selectedCommitment.commitmentId,
					field: "sourceCount",
					value: response?.length || 0,
				})
			);
		} catch (error) {
			console.error("Error associating content:", error);
			createNotification({
				type: "error",
				message: translate("commitment-tracking.details.link-error"),
			});
		}
	};
	const getValue = (field) => {
		if (Array.isArray(field)) {
			return field.length > 0 ? field[0] : "-";
		}
		return field || "-";
	};
	const displayPilot = () => {
		if (!selectedCommitment?.pilot) {
			return "-";
		}
		const displayName = getPilotDisplayName(selectedCommitment.pilot);
		return <CustomTooltip title={displayName}>{displayName}</CustomTooltip> || "-";
	};
	const displayDate = (dateValue) => {
		if (!dateValue) {
			return "-";
		}
		return formatDate(getValue(dateValue));
	};
	const getDocumentReference = (source) => {
		const { details } = source;
		if (!details) {
			return "";
		}
		const { documentName, clause } = details;
		if (clause === ">ROOT" || !clause) {
			return documentName || "";
		}
		return `${documentName} | ${clause}`;
	};
	const getContentType = (source) => {
		const { type } = source;
		if (!type) {
			return "";
		}
		if (type === "COMMITMENT") {
			return translate("commitment-tracking.content-type.commitment");
		}
		return translate("commitment-tracking.content-type.penalty");
	};
	const handleLike = (newStatus, source) => {
		const { informationId } = source || {};
		if (hasRights && projectId && informationId && selectedCommitment) {
			const statusToChange = newStatus !== source?.isVerified ? newStatus : null;
			likeDetectedContent(
				{ projectId, commitmentId: selectedCommitment.commitmentId, informationId },
				statusToChange
			)
				.then(() => {
					setSources((prev) =>
						prev.map((originalSource) =>
							originalSource.informationId === informationId
								? { ...originalSource, isVerified: statusToChange }
								: originalSource
						)
					);
				})
				.catch(console.error);
		}
	};

	const wordingLikeButtons = (likeStatus, source) => {
		if (hasRights) {
			if (likeStatus) {
				if (source?.isVerified) {
					return translate("commitment-tracking.commitment-details.like.allowed.validated");
				}
				return translate("commitment-tracking.commitment-details.like.allowed.neutral");
			}
			if (source?.isVerified || source?.isVerified === null) {
				return translate("commitment-tracking.commitment-details.dislike.allowed.neutral");
			}
			return translate("commitment-tracking.commitment-details.dislike.allowed.non-validated");
		}
		if (source?.isVerified) {
			return translate("commitment-tracking.commitment-details.any.non-allowed.validated");
		}
		if (source?.isVerified === false) {
			return translate("commitment-tracking.commitment-details.any.non-allowed.non-validated");
		}
		return translate("commitment-tracking.commitment-details.any.non-allowed.neutral");
	};

	if (!selectedCommitment) {
		const isMultiSelect = selectedRows && selectedRows.length > 1;
		return (
			<div className={styles.emptyContainer}>
				{!launchProgress && (
					<div className={styles.emptyContent}>
						<p>
							{isMultiSelect
								? translate("commitment-tracking.empty-state.multi-select")
								: translate("commitment-tracking.empty-state.no-commitment-selected")}
						</p>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h2 className={styles.title}>
					{selectedCommitment?.name || "-"}
					<CustomIconButton
						btnClassName={styles.infoIcon}
						color="secondary"
						icon={icon[(selectedCommitment?.nameIsGeneratedByAI && "faWandMagicSparkles") || "faPen"]}
						iconColor="var(--color-blue)"
						size="xs"
						tooltip={translate(
							(selectedCommitment?.nameIsGeneratedByAI && "commitment-tracking.details.added-by-ai") ||
								"commitment-tracking.details.added-manually"
						)}
						tooltipPlacement="bottom"
						variant="text"
					/>
				</h2>
				<CustomTooltip title={translate("commitment-tracking.details.menu.tooltip")}>
					<span>
						<CustomButton
							color="secondary"
							startIcon={icon.faEllipsisH}
							variant="outlined"
							onClick={handleMenuClick}
						/>
					</span>
				</CustomTooltip>
				<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
					<div className={styles.menuTitle}>{translate("commitment-tracking.details.menu-title")}</div>
					<MenuItem className={styles.menuItem} onClick={handleEditClick}>
						<IconComponent color="var(--grey-500)" icon={icon.faPen} />
						{translate("commitment-tracking.details.menu-edit")}
					</MenuItem>
					<MenuItem
						className={`${styles.menuItem} ${styles.pbottom}`}
						color="var(--grey-500)"
						onClick={handleDeleteClick}
					>
						<IconComponent color="var(--grey-500)" icon={icon.faTrash} />
						{translate("commitment-tracking.details.menu-delete")}
					</MenuItem>
					<MenuItem className={styles.menuItem} onClick={handleCommentsClick}>
						<IconComponent color="var(--grey-500)" icon={icon.faComments} />
						{translate("commitment-tracking.details.menu-comments")}
					</MenuItem>
					<MenuItem className={styles.menuItem} onClick={handleHistoryClick}>
						<IconComponent color="var(--grey-500)" icon={icon.faHistory} />
						{translate("commitment-tracking.details.menu-history")}
					</MenuItem>
				</Menu>
			</div>
			<section className={styles.section}>
				<div className={styles.infoGrid}>
					<div className={styles.infoColumn}>
						<span className={styles.label}>{translate("commitment-tracking.details.pilot-label")}</span>
						<span className={styles.value}>{displayPilot()}</span>
					</div>
					<div className={styles.infoColumn}>
						<span className={styles.label}>{translate("commitment-tracking.details.deadline-label")}</span>
						<span className={styles.value}>{displayDate(selectedCommitment.completionDeadline)}</span>
					</div>
					<div className={styles.infoColumn}>
						<span className={styles.label}>{translate("commitment-tracking.filter.frequency")}</span>
						<span className={styles.value}>
							{(selectedCommitment?.frequency &&
								translateFilterOptionLabel("frequency", selectedCommitment.frequency)) ||
								"-"}
						</span>
					</div>
				</div>
				<div className={styles.infoGrid}>
					<div className={styles.infoColumn}>
						<span className={styles.label}>{translate("commitment-tracking.filter.priority")}</span>
						<span className={styles.value}>
							{(selectedCommitment?.priority &&
								translateFilterOptionLabel("priority", selectedCommitment.priority)) ||
								"-"}
						</span>
					</div>
					<div className={styles.infoColumn}>
						<span className={styles.label}>{translate("commitment-tracking.details.risk-label")}</span>
						<span className={styles.value}>
							{(selectedCommitment?.risk &&
								translateFilterOptionLabel("risk", selectedCommitment.risk)) ||
								"-"}
						</span>
					</div>
					<div className={styles.infoColumn} />
				</div>
			</section>
			<section className={styles.section}>
				<span className={styles.sectionTitle}>
					{translate("commitment-tracking.details.detail-label")}
					<CustomIconButton
						btnClassName={styles.infoIcon}
						color="secondary"
						icon={icon[(selectedCommitment?.detailsIsGeneratedByAI && "faWandMagicSparkles") || "faPen"]}
						iconColor="var(--color-blue)"
						size="xs"
						tooltip={translate(
							(selectedCommitment?.detailsIsGeneratedByAI && "commitment-tracking.details.added-by-ai") ||
								"commitment-tracking.details.added-manually"
						)}
						tooltipPlacement="bottom"
						variant="text"
					/>
				</span>
				<p className={styles.description}>{selectedCommitment.details || "-"}</p>
			</section>
			<section className={styles.contentSection}>
				<div className={styles.sectionHeader}>
					<div
						aria-expanded={isContentExpanded}
						className={styles.sectionTitleWrapper}
						role="button"
						tabIndex={0}
						onClick={toggleContentSection}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								toggleContentSection();
							}
						}}
					>
						<IconComponent
							className={styles.expandIcon}
							icon={isContentExpanded ? icon.faChevronDown : icon.faChevronRight}
						/>
						<span className={styles.sectionTitle}>
							{translate("commitment-tracking.details.detected-content-title")}
						</span>
					</div>
					<div className={styles.headerActions}>
						<CustomButton
							color="secondary"
							startIcon={icon.faPlus}
							variant="outlined"
							onClick={handleAssociateContent}
						>
							{translate("commitment-tracking.details.associate-content-btn")}
						</CustomButton>
					</div>
				</div>
				{isContentExpanded && (
					<div className={styles.contentList}>
						{isLoadingSources && (
							<div className={styles.loadingState}>
								{translate("commitment-tracking.empty-state.loading")}
							</div>
						)}
						{!isLoadingSources && sources.length === 0 && (
							<div className={styles.emptySourcesState}>
								<IconComponent color="var(--blue-500)" icon={icon.faInfoCircle} />
								{translate("commitment-tracking.empty-state.no-sources")}
							</div>
						)}
						{!isLoadingSources &&
							sources.map((source) => (
								<div key={source.informationId} className={styles.contentItem}>
									<div className={styles.contentHeader}>
										<span className={styles.contentDoc}>{getDocumentReference(source)}</span>
										<div className={styles.contentActions}>
											{source.isManuallyAdded ? (
												<CustomIconButton
													btnClassName={styles.originButton}
													className={styles.originButton}
													color="secondary"
													icon={icon.faPen}
													iconColor="var(--grey-700)"
													size="xs"
													tooltip={translate("commitment-tracking.details.added-by-user", {
														user: source.userDisplayName || "",
													})}
													tooltipPlacement="bottom"
													variant="text"
												/>
											) : (
												<CustomIconButton
													btnClassName={styles.originButton}
													className={styles.originButton}
													color="secondary"
													icon={icon.faWandMagicSparkles}
													iconColor="var(--blue-500)"
													size="xs"
													tooltip={translate("commitment-tracking.details.added-by-ai")}
													tooltipPlacement="bottom"
													variant="text"
												/>
											)}
											<div className={styles.actionSeparator} />
											<CustomIconButton
												color="secondary"
												icon={icon.faTrash}
												iconColor="var(--grey-700)"
												size="sm"
												tooltip={translate(
													"commitment-tracking.details.delete-content-tooltip"
												)}
												tooltipPlacement="bottom"
												variant="outlined"
												onClick={() => handleDeleteContentClick(source.informationId)}
											/>
											<CustomIconButton
												color="secondary"
												icon={icon.faFile}
												iconColor="var(--grey-700)"
												size="sm"
												tooltip={translate("common:btn.display")}
												tooltipPlacement="bottom"
												variant="outlined"
												onClick={() => {
													setPreview({
														coordinates: source.details?.rectangleDTO || null,
														documentId: source.details?.documentId,
														page: source.details?.page,
														informationId: source.informationId,
														informationType: source.details?.type,
													});
												}}
											/>
										</div>
									</div>
									{isSegFeatureEnabled(SegFlags.DISABLED) && (
										<div className={styles.contentBadge}>
											<span>{getContentType(source)}</span>
										</div>
									)}
									<div className={styles.contentText}>{source.details?.content || ""}</div>
									<div className={styles.likeButtons}>
										<CustomTooltip
											title={!hasRights && translate("common:permissions.insufficient-rights")}
										>
											<span>
												<CustomIconButton
													btnClassName={styles.like__button}
													data-has-rights={hasRights}
													data-is-on={!!source?.isVerified}
													disabled={!hasRights}
													icon={icon.faThumbsUp}
													iconColor={
														(source?.isVerified && "var(--color-white)") || undefined
													}
													size="xs"
													tooltip={wordingLikeButtons(true, source)}
													variant="outlined"
													onClick={() => {
														handleLike(true, source);
													}}
												/>
											</span>
										</CustomTooltip>
										<CustomTooltip
											title={!hasRights && translate("common:permissions.insufficient-rights")}
										>
											<span>
												<CustomIconButton
													btnClassName={styles.dislike__button}
													data-has-rights={hasRights}
													data-is-on={source?.isVerified === false}
													disabled={!hasRights}
													icon={icon.faThumbsDown}
													iconColor={
														(source?.isVerified === false && "var(--color-white)") ||
														undefined
													}
													size="xs"
													tooltip={wordingLikeButtons(false, source)}
													variant="outlined"
													onClick={() => {
														handleLike(false, source);
													}}
												/>
											</span>
										</CustomTooltip>
									</div>
								</div>
							))}
					</div>
				)}
			</section>
			<AssociateContentDialog
				open={linkDialogOpen}
				projectId={projectId}
				onClose={handleLinkDialogClose}
				onSubmit={handleLinkSubmit}
			/>
			<ConfirmationDialog
				cancelLabel={translate("commitment-tracking.details.delete-cancel-button")}
				confirmLabel={translate("commitment-tracking.details.delete-confirm-button")}
				iconColor="var(--red-500)"
				iconType={icon.faExclamationTriangle}
				message={translate("commitment-tracking.dialog.delete.message")}
				open={deleteDialogOpen}
				onClose={handleDeleteCancel}
				onConfirm={handleDeleteConfirm}
			/>
			<ConfirmationDialog
				cancelLabel={translate("commitment-tracking.details.delete-content-cancel-button")}
				confirmLabel={translate("commitment-tracking.details.delete-content-confirm-button")}
				iconColor="var(--red-500)"
				iconType={icon.faExclamationTriangle}
				message={translate("commitment-tracking.dialog.delete-content.message")}
				open={deleteContentDialogOpen}
				title={translate("commitment-tracking.dialog.delete-content.title")}
				onClose={handleDeleteContentCancel}
				onConfirm={handleDeleteContentConfirm}
			/>
			<Preview
				coordinates={preview?.coordinates}
				docId={preview?.documentId}
				infoId={preview?.informationId}
				infoType={preview?.informationType}
				open={!!preview}
				page={preview?.page}
				projectId={projectId}
				onClose={() => setPreview(null)}
			/>
		</div>
	);
}
